import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { GraduationCap, Target, Sparkles } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { getTimeline, getMessages } from "../lib/api";

export default function About() {
  const [timeline, setTimeline] = useState([]);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    getTimeline().then(setTimeline).catch(() => {});
    getMessages().then(setMessages).catch(() => {});
  }, []);

  return (
    <div data-testid="about-page">
      <PageHeader
        eyebrow="Our Story"
        title="A legacy of education, inclusion, and service."
        subtitle="Since 1973, Jawad Ali Shah Imambara Girls Degree College has been nurturing girls who shape their families, communities, and the nation."
      />

      {/* Our Campus */}
      <section className="py-16 lg:py-24 bg-brand-ivory" data-testid="campus-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-8 lg:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-3 max-w-3xl mx-auto rounded-3xl overflow-hidden shadow-[0_20px_50px_rgba(15,76,58,0.15)]"
          >
            <img
              src="/uploads/College-Building.jpg"
              alt="Our college campus building"
              className="w-full h-auto block"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2"
          >
            <div className="eyebrow mb-2">Our Campus</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-brand-primary leading-tight mb-4">
              A space that feels like home.
            </h2>
            <p className="text-brand-ink/80 leading-relaxed mb-4">
              Our spacious campus features modern classrooms, a dedicated
              library, computer lab, and open green spaces — designed to provide
              a calm, focused environment for our students to thrive.
            </p>
            <p className="text-brand-ink/70 leading-relaxed text-sm">
              The college operates from a multi-storey building with classrooms
              encircling a central courtyard, ensuring natural light, fresh air,
              and a tranquil atmosphere for learning.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="py-16 lg:py-24 bg-brand-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="eyebrow mb-2">The Beginning</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-brand-primary leading-tight mb-6">
              Rooted in philanthropy since 1973.
            </h2>
            <p className="text-brand-ink/80 leading-relaxed mb-4">
              Shri Jawad Ali Shah, with a vision to empower the daughters of the
              region, donated land and agricultural property to establish this
              college — dedicated especially to the higher education of Muslim
              minority girls at the time, and now welcoming students from every
              community.
            </p>
            <p className="text-brand-ink/80 leading-relaxed">
              Over the decades, the institution has evolved into a postgraduate
              college affiliated with DDU Gorakhpur University, earning
              grant-in-aid status and a reputation for nurturing confident,
              capable girls.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="grid gap-4"
          >
            {[
              { icon: Target, title: "Mission", text: "To provide quality higher education to girls and empower them as independent, thoughtful citizens who contribute meaningfully to society." },
              { icon: Sparkles, title: "Vision", text: "To be a nurturing beacon of girls' education that blends heritage values with modern excellence." },
              { icon: GraduationCap, title: "Philosophy", text: "Every student deserves a classroom that respects her background, challenges her intellect, and prepares her for a life of dignity and service." },
            ].map((b, i) => (
              <div
                key={b.title}
                className="bg-white rounded-3xl p-6 lg:p-7 border border-brand-primary/10 shadow-[0_8px_30px_rgba(15,76,58,0.05)]"
                data-testid={`about-block-${b.title.toLowerCase()}`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-brand-accent text-brand-primary flex items-center justify-center">
                    <b.icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-serif text-xl text-brand-primary">{b.title}</h3>
                </div>
                <p className="text-sm text-brand-ink/75 leading-relaxed">{b.text}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Timeline */}
      {timeline.length > 0 && (
        <section
          className="py-16 lg:py-24 bg-brand-accent/40"
          data-testid="timeline-section"
        >
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 lg:mb-14 text-center">
              <div className="eyebrow mb-2">Our Journey</div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-brand-primary leading-tight">
                Milestones through the decades.
              </h2>
            </div>
            <div className="relative">
              <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-brand-primary/20" />
              <div className="space-y-10">
                {timeline.map((e, i) => (
                  <motion.div
                    key={e.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.05 }}
                    className={`flex gap-4 md:gap-0 items-start ${
                      i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                    }`}
                    data-testid={`timeline-${e.year}`}
                  >
                    <div className={`flex-1 ${i % 2 === 0 ? "md:pr-10 md:text-right" : "md:pl-10"} hidden md:block`} />
                    <div className="relative flex-shrink-0 w-12 md:w-0 flex justify-center">
                      <div className="w-4 h-4 rounded-full bg-brand-secondary ring-4 ring-brand-ivory absolute md:left-1/2 md:-translate-x-1/2 top-2 z-10" />
                    </div>
                    <div className={`flex-1 ${i % 2 === 0 ? "md:pl-10" : "md:pr-10 md:text-right"}`}>
                      <div className="bg-white rounded-3xl p-6 border border-brand-primary/10 shadow-[0_8px_30px_rgba(15,76,58,0.05)] inline-block">
                        <div className="font-display text-3xl lg:text-4xl text-brand-secondary mb-1">
                          {e.year}
                        </div>
                        <h3 className="font-serif text-xl text-brand-primary mb-2">
                          {e.title}
                        </h3>
                        <p className="text-sm text-brand-ink/75 leading-relaxed max-w-md">
                          {e.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Full Messages */}
      {messages.length > 0 && (
        <section className="py-16 lg:py-24 bg-brand-ivory" data-testid="messages-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12 lg:space-y-20">
            {messages.map((m, i) => (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={`grid lg:grid-cols-12 gap-8 lg:gap-16 items-start ${
                  i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
                }`}
                data-testid={`leader-card-${i}`}
              >
                {/* Portrait image side */}
                <div className="lg:col-span-4 relative">
                  <div className="relative max-w-sm mx-auto lg:mx-0">
                    {/* Decorative gold frame behind */}
                    <div
                      className={`absolute -inset-3 rounded-[2rem] bg-brand-secondary/30 ${
                        i % 2 === 1 ? "rotate-2" : "-rotate-2"
                      }`}
                      aria-hidden="true"
                    />
                    {/* Photo */}
                    <div className="relative aspect-[4/5] rounded-[1.75rem] overflow-hidden bg-brand-primary border-4 border-brand-secondary shadow-[0_30px_60px_-20px_rgba(15,76,58,0.4)]">
                      {m.photo ? (
                        <img
                          src={m.photo}
                          alt={m.from_name}
                          className="w-full h-full object-cover object-top"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-brand-secondary/60">
                          <GraduationCap className="w-32 h-32" strokeWidth={1.2} />
                        </div>
                      )}
                      {/* Gold corner ornament */}
                      <svg
                        className="absolute top-3 left-3 w-10 h-10 text-brand-secondary/80"
                        viewBox="0 0 40 40"
                        fill="none"
                      >
                        <path d="M2 14V2H14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                      </svg>
                      <svg
                        className="absolute bottom-3 right-3 w-10 h-10 text-brand-secondary/80"
                        viewBox="0 0 40 40"
                        fill="none"
                      >
                        <path d="M38 26V38H26" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                      </svg>
                    </div>
                    {/* Name plate beneath */}
                    <div className="mt-6 text-center lg:text-left">
                      <div className="font-display text-3xl lg:text-4xl text-brand-primary leading-tight">
                        {m.from_name}
                      </div>
                      <div className="text-xs tracking-[0.3em] uppercase text-brand-secondary font-semibold mt-2">
                        {m.from_role}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Message text side */}
                <div className="lg:col-span-8 lg:pt-8">
                  <div className="text-brand-secondary text-7xl lg:text-8xl font-display leading-none mb-4">
                    &ldquo;
                  </div>
                  <p className="font-serif italic text-2xl lg:text-3xl text-brand-primary leading-relaxed mb-6">
                    {m.message.split(".")[0]}.
                  </p>
                  <div className="space-y-4 text-brand-ink/80 leading-relaxed text-base lg:text-lg">
                    {m.message
                      .split(".")
                      .slice(1)
                      .filter((s) => s.trim())
                      .map((sentence, j) => (
                        <p key={j}>{sentence.trim()}.</p>
                      ))}
                  </div>
                  <div className="mt-8 inline-flex items-center gap-3 text-sm text-brand-muted">
                    <span className="h-px w-12 bg-brand-secondary" />
                    <span className="tracking-wide">{m.from_name}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
